import React from "react";
import PropTypes from "prop-types";

import CardScope, { useCardScope } from "./index";
import CardBusy from "./CardBusy";

/**
 * The wrapper that makes any card scopeable.
 *
 * It answers the three questions once — which workspaces, which dates,
 * averaged or totalled — draws the bar that says the answer and lets the
 * viewer change it, then hands the numbers to whatever the card draws.
 */
const ScopedCard = ({ settings, ownWorkspaceId, editing, onSettings, empty, farRight, needsKpis, boardWs, boardWindow, boardCustom, title, inheritWorkspaceIds, children }) => {
  const scope = useCardScope(settings, ownWorkspaceId, boardWs, { window: boardWindow, custom: boardCustom }, inheritWorkspaceIds);
  const busy = !!(scope.loading || scope.refreshing);
  const wrapCls = `cs-wrap${farRight ? " cs-far" : ""}${scope.refreshing ? " cs-is-refreshing" : ""}`;
  const showScope = !scope.navControlled;

  const { pick } = scope;
  const setPick = (v) => {
    scope.choose(v);
    if (onSettings) onSettings({ ...settings, cardPick: v });
  };

  let { kpis } = scope;
  let { teamPts } = scope;
  if (scope.byPlayer && pick !== "all") {
    const who = scope.people.find((p) => String(p.userId) === String(pick));
    kpis = scope.kpis.map((k) => ({
      ...k,
      amount: Number(who?.kpis?.[String(k._id)]?.amt) || 0,
      points: Number(who?.kpis?.[String(k._id)]?.pts) || 0,
    }));
    teamPts = Number(who?.pts) || 0;
  }

  const yetiPts = Number(scope.yeti?.month?.total?.pts ?? scope.yeti?.month?.total ?? 0) || 0;

  const busyLabel = scope.loading ? "Loading metrics…" : "Updating for date range…";

  if (typeof children !== "function") {
    return (
      <div className={wrapCls}>
        {showScope && <CardScope scope={{ ...scope, pick }} onPick={setPick} editing={editing} />}
        <CardBusy active={busy} cover={!!scope.loading} label={busyLabel} />
        {children}
      </div>
    );
  }

  const emptyState = (heading, message) => (
    <div className="cs-emptyframe">
      {title && <div className="cs-emptyframe-h">{title}</div>}
      <div className="cs-empty">
        <b>{heading}</b>
        {message}
      </div>
    </div>
  );

  let body;
  if (scope.loading && needsKpis) {
    body = emptyState(title || "Loading…", "");
  } else if (kpis.length || !needsKpis) {
    body = children({ ...scope, kpis, teamPts, yetiPts, pick });
  } else {
    const crossed = scope.reading.length > 1;
    body = crossed
      ? emptyState(
        "No metric in common",
        "These workspaces don’t track the same metrics, so there is nothing fair to average. Align them and the card fills in.",
      )
      : emptyState(
        empty?.title || "No live metrics yet",
        empty?.body || "Mark a metric live on the workspace and it shows up here.",
      );
  }

  return (
    <div className={wrapCls}>
      {showScope && <CardScope scope={{ ...scope, pick }} onPick={setPick} editing={editing} />}
      <CardBusy active={busy} cover={!!scope.loading} label={busyLabel} />
      {body}
    </div>
  );
};

ScopedCard.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  settings: PropTypes.object,
  ownWorkspaceId: PropTypes.string,
  editing: PropTypes.bool,
  onSettings: PropTypes.func,
  empty: PropTypes.shape({ title: PropTypes.string, body: PropTypes.string }),
  farRight: PropTypes.bool,
  needsKpis: PropTypes.bool,
  boardWs: PropTypes.string,
  boardWindow: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  boardCustom: PropTypes.object,
  title: PropTypes.string,
  inheritWorkspaceIds: PropTypes.arrayOf(PropTypes.string),
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]).isRequired,
};
ScopedCard.defaultProps = {
  settings: {},
  ownWorkspaceId: null,
  editing: false,
  onSettings: null,
  empty: null,
  farRight: false,
  needsKpis: true,
  boardWs: null,
  boardWindow: null,
  boardCustom: null,
  title: null,
  inheritWorkspaceIds: [],
};

export default ScopedCard;
